// app/src/lib/revenue/cogs.js — real-time food cost (US-4.3).

import { db } from '../db.js';
import { requireUserId } from '../procurement/util.js';
import { CATEGORIES as ALL_CATEGORIES } from '../procurement/items.js';
import { settledNetByMonth } from './settledSales.js';
import { creditAccountIds, withoutCreditTxs } from '../cashflow/accounts.js';

/** Legacy item-level categories (normalized catalog items). */
const COGS_CATEGORIES = new Set(['produce', 'dairy', 'meat', 'seafood', 'dry-goods', 'bakery', 'beverage', 'alcohol']);

// Top-level COGS buckets carried on the line item itself.
// COGS total = food + beverages + packaging; `other` (cleaning, linen/laundry,
// equipment, smallwares) is tracked but EXCLUDED from COGS.
const COGS_TOP = new Set(['food', 'beverages', 'packaging']);

/** Best-effort top-level category for a line with no stored category (old scans). */
export function categorizeLine(description) {
  const s = String(description || '').toLowerCase();
  if (/(juice|soda|cola| pop |\btea\b|coffee|espresso|latte|wine|beer|\bale\b|lager|liquor|vodka|whisk|\brum\b|\bgin\b|tequila|bourbon|lemonade|kombucha|cider|seltzer|sparkling)/.test(s)) return 'beverages';
  if (/(bag|box|container|clamshell|\bcup\b|\blid\b|straw|napkin|tray|carton|foil|film|wrap|to.?go|takeout|cutlery|disposable)/.test(s)) return 'packaging';
  if (/(clean|soap|detergent|sanitiz|bleach|linen|towel|apron|uniform|laundry|\bglove|equipment|smallware)/.test(s)) return 'other';
  return 'food';
}

/** Default gauge thresholds (pts over target). */
export const GAUGE_AMBER_POINTS = 5;

/** 'YYYY-MM' keys covered by [from, to], ascending. */
function monthsCovered(from, to) {
  const out = [];
  let m = from.slice(0, 7);
  const last = to.slice(0, 7);
  while (m <= last) {
    out.push(m);
    const [y, mm] = m.split('-').map(Number);
    m = new Date(Date.UTC(y, mm, 1)).toISOString().slice(0, 7);
  }
  return out;
}

/**
 * Food cost % for the date range.
 *
 * `settled: true` replaces the deposit-dated sales denominator with the settled
 * monthly net (revenue/settledSales) — each boundary deposit credited to the
 * month its sales were earned. Pass it ONLY for whole-calendar-month ranges
 * (from = the 1st; to = month end, or today for an in-progress month); rolling
 * windows should stay deposit-dated.
 *
 * @param {{ from:string, to:string, targetPct?:number, includeWaste?:boolean, settled?:boolean }} window
 * @returns {Promise<{
 *   from:string, to:string,
 *   cogsCents:number, netSalesCents:number, rawNetSalesCents:number,
 *   foodCostPct:number|null,
 *   targetPct:number|null, status:'green'|'amber'|'red'|'no-sales',
 *   drilldown: Array<{ itemId:string, itemName:string, totalCents:number }>,
 * }>}
 */
export async function calc(window) {
  const userId = await requireUserId();
  if (!isoOk(window.from) || !isoOk(window.to)) throw new Error('cogs.calc: invalid date range');

  // Sales in window. Inclusive bounds on both ends so `from === to` (single-day
  // queries — common for "today" food cost) still match same-day entries.
  const salesRows = await db.salesEntries
    .where('[userId+date]').between([userId, window.from], [userId, window.to], true, true)
    .toArray();
  const rawNetSalesCents = salesRows.reduce((s, e) => s + (e.netSalesCents || 0), 0);
  let netSalesCents = rawNetSalesCents;
  if (window.settled) {
    const months = monthsCovered(window.from, window.to);
    const settled = await settledNetByMonth(months);
    netSalesCents = months.reduce((s, m) => s + (settled[m] || 0), 0);
  }

  // Find invoices issued in window so we can pull their line items.
  const invoices = await db.invoices.where('userId').equals(userId)
    .filter((i) => !i.isDuplicate && i.issuedDate >= window.from && i.issuedDate <= window.to)
    .toArray();
  const invoiceIds = invoices.map((i) => i.invoiceId);
  const lineItems = invoiceIds.length
    ? await db.lineItems.where('invoiceId').anyOf(invoiceIds).toArray()
    : [];

  // Resolve item categories. Only items in COGS categories are counted.
  const itemIds = Array.from(new Set(lineItems.map((li) => li.itemId).filter(Boolean)));
  const items = itemIds.length ? await db.items.where('itemId').anyOf(itemIds).toArray() : [];
  const itemById = new Map(items.map((i) => [i.itemId, i]));

  const byItem = new Map();
  const byCategory = { food: 0, beverages: 0, packaging: 0, other: 0 };
  let cogsCents = 0;
  for (const li of lineItems) {
    const cents = li.extendedPriceCents || 0;
    // Top-level category: stored on the line (set by OCR) or inferred from the
    // description for older scans. Counts the line directly — no catalog link needed.
    let cat = li.category;
    if (!Object.prototype.hasOwnProperty.call(byCategory, cat)) cat = categorizeLine(li.description);
    byCategory[cat] += cents;
    if (!COGS_TOP.has(cat)) continue; // 'other' is not COGS
    cogsCents += cents;
    const key = li.itemId || ('d:' + String(li.description || '').toLowerCase());
    const name = (li.itemId && itemById.get(li.itemId)) ? itemById.get(li.itemId).name : (li.description || 'Item');
    const cur = byItem.get(key) || { itemId: key, itemName: name, totalCents: 0 };
    cur.totalCents += cents;
    byItem.set(key, cur);
  }

  // Hand-categorized COGS card charges (Spending tab): an unscanned ingredient
  // run the operator marked COGS counts toward food cost even though it never
  // produced line items. A charge RECONCILED to a scanned invoice is excluded —
  // that invoice's line items above already carry the spend (counting the
  // charge too would double it). Explicit decisions only (rule/override); the
  // keyword guess never reaches COGS.
  let unscannedCardCents = 0;
  try {
    const creditSet = await creditAccountIds(userId);
    if (creditSet.size > 0) {
      const { explicitCategory, getRules } = await import('../cashflow/spendCategory.js');
      const rules = await getRules(userId);
      const reconciledTxIds = new Set(
        (await db.invoices.where('userId').equals(userId).filter((i) => !!i.plaidTxId).toArray()).map((i) => i.plaidTxId),
      );
      const cardTxs = await db.plaidTransactions
        .where('[userId+date]').between([userId, window.from], [userId, window.to], true, true)
        .toArray();
      for (const t of cardTxs) {
        if (!creditSet.has(t.plaidAccountId) || t.pending) continue;
        if ((t.amountCents || 0) <= 0) continue;
        if (reconciledTxIds.has(t.plaidTxId)) continue; // invoice lines already counted
        if (explicitCategory(t, { reconciledTxIds: new Set(), rules }) !== 'cogs') continue;
        unscannedCardCents += t.amountCents || 0;
      }
      cogsCents += unscannedCardCents;
      byCategory.food += unscannedCardCents;
    }
  } catch (e) { console.warn('[cogs] unscanned card COGS skipped', e); }

  // Waste contributes to COGS for the period if requested (default true).
  if (window.includeWaste !== false) {
    const wasteRows = await db.wasteEntries
      .where('[userId+date]').between([userId, window.from], [userId, window.to], true, true)
      .toArray();
    for (const w of wasteRows) {
      cogsCents += w.valuedCents || 0;
      byCategory.food += w.valuedCents || 0;
      if (!w.itemId) continue;
      const item = itemById.get(w.itemId) || await db.items.get(w.itemId);
      if (!item) continue;
      const cur = byItem.get(item.itemId) || { itemId: item.itemId, itemName: item.name + ' (waste)', totalCents: 0 };
      cur.totalCents += w.valuedCents || 0;
      byItem.set(item.itemId, cur);
    }
  }

  const foodCostPct = netSalesCents > 0 ? cogsCents / netSalesCents : null;
  let status = 'no-sales';
  const targetPct = window.targetPct != null ? window.targetPct : null;
  if (foodCostPct != null && targetPct != null) {
    const overPoints = (foodCostPct - targetPct) * 100;
    if (overPoints <= 0) status = 'green';
    else if (overPoints <= GAUGE_AMBER_POINTS) status = 'amber';
    else status = 'red';
  } else if (foodCostPct != null) {
    status = 'amber'; // no target set; surface as caution.
  }

  const drilldown = Array.from(byItem.values()).sort((a, b) => b.totalCents - a.totalCents).slice(0, 20);

  return { from: window.from, to: window.to, cogsCents, netSalesCents, rawNetSalesCents, foodCostPct, targetPct, status, drilldown, byCategory, unscannedCardCents };
}

/**
 * Weekly food-cost trend (ISO weeks, Mon→Sun). Each entry covers a full week
 * (the current week is week-to-date). Aggregating across the week absorbs the
 * deliveries-vs-usage and deposit-timing noise that makes the daily trend spiky.
 */
export async function trendWeekly({ weeks = 12, targetPct } = {}) {
  const userId = await requireUserId();
  const today = new Date();
  // Find this week's Monday (UTC).
  const thisMon = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  thisMon.setUTCDate(thisMon.getUTCDate() - ((thisMon.getUTCDay() + 6) % 7));
  const todayIso = today.toISOString().slice(0, 10);

  // Pull all inflow transactions across the trailing window once and bucket by
  // ISO week — so we can ALSO compute COGS / total bank deposits per week (a
  // rougher but useful point of reference: it doesn't depend on channel mapping
  // and absorbs cash deposits we hadn't classified as sales). DEPOSITORY only:
  // a credit on the card account (autopay payment credit, purchase refund) is
  // not a bank deposit and would inflate the reference.
  const oldestStart = new Date(thisMon); oldestStart.setUTCDate(thisMon.getUTCDate() - (weeks - 1) * 7);
  const oldestIso = oldestStart.toISOString().slice(0, 10);
  const allInflows = withoutCreditTxs(await db.plaidTransactions
    .where('[userId+date]').between([userId, oldestIso], [userId, todayIso], true, true)
    .filter((t) => (t.amountCents || 0) < 0 && !t.pending)
    .toArray()
    .catch(() => []), await creditAccountIds(userId).catch(() => new Set()));
  const depositsByWeekStart = {};
  for (const t of allInflows) {
    const d = new Date(t.date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    const wk = d.toISOString().slice(0, 10);
    depositsByWeekStart[wk] = (depositsByWeekStart[wk] || 0) + Math.abs(t.amountCents || 0);
  }

  const out = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const start = new Date(thisMon); start.setUTCDate(thisMon.getUTCDate() - i * 7);
    const end = new Date(start); end.setUTCDate(start.getUTCDate() + 6);
    const fromIso = start.toISOString().slice(0, 10);
    const rawTo = end.toISOString().slice(0, 10);
    // Cap the current (or any future-overlapping) week to today so the live week is WTD.
    const toIso = rawTo > todayIso ? todayIso : rawTo;
    const r = await calc({ from: fromIso, to: toIso, targetPct });
    const depositCents = depositsByWeekStart[fromIso] || 0;
    const depositPct = depositCents > 0 ? r.cogsCents / depositCents : null;
    out.push({ weekStart: fromIso, weekEnd: rawTo, partial: i === 0,
      foodCostPct: r.foodCostPct, cogsCents: r.cogsCents, netSalesCents: r.netSalesCents,
      depositCents, depositPct });
  }
  return out;
}

function isoOk(s) { return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s); }
